import React from 'react';
import {
    Modal,
    Box,
    Typography,
    IconButton,
    Button,
    Divider,
    Paper,
    Stack,
    Grid
} from '@mui/material';
import { Icon } from "@iconify/react";
import closeIcon from '@iconify/icons-mdi/close-circle-outline';
import { DateTime } from 'luxon';

const ExpenseDetailsPopup = ({ open, onClose, expense }) => {
    if (!expense) return null;

    const DetailItem = ({ icon, label, value, color = "#6366f1" }) => (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: '16px',
                bgcolor: 'transparent',
                borderColor: '#f1f5f9',
                transition: 'all 0.2s',
                '&:hover': {
                    bgcolor: '#f8fafc',
                    borderColor: '#e2e8f0'
                }
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '12px',
                    bgcolor: `${color}10`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: color,
                    flexShrink: 0
                }}>
                    <Icon icon={icon} width={22} />
                </Box>
                <Box sx={{ overflow: 'hidden' }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {label}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {value || '—'}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{
                backdrop: { sx: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(15, 23, 42, 0.4)' } }
            }}
        >
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 550 }, maxHeight: '90vh', bgcolor: '#ffffff', borderRadius: '28px',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid rgba(255,255,255,0.1)',
                outline: 'none', overflow: 'hidden', display: 'flex', flexDirection: 'column'
            }}>
                {/* Premium Header */}
                <Box sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #3c0008 0%, #3c0008 100%)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Decorative Background Icon */}
                    <Icon
                        icon="mdi:cash-multiple"
                        width={120}
                        style={{
                            position: 'absolute',
                            right: -20,
                            bottom: -30,
                            opacity: 0.1,
                            transform: 'rotate(-15deg)'
                        }}
                    />

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            p: 1.5,
                            borderRadius: '16px',
                            bgcolor: 'rgba(255,255,255,0.1)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <Icon icon="mdi:receipt-text" width={28} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="900" sx={{ lineHeight: 1.2 }}>Expense Details</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 600 }}>
                                INVOICE: {expense.invoiceNo || 'N/A'}
                            </Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={onClose} sx={{
                        color: '#ffffff',
                        bgcolor: 'rgba(255,255,255,0.05)',
                        '&:hover': { transform: 'rotate(90deg)', bgcolor: 'rgba(255,255,255,0.15)' },
                        transition: '0.3s'
                    }}>
                        <Icon icon={closeIcon} width={24} />
                    </IconButton>
                </Box>

                <Box sx={{ p: 3, overflowY: 'auto' }}>
                    <Stack spacing={3}>
                        {/* Main Info Card */}
                        <Paper sx={{ p: 3, borderRadius: '20px', bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                                        Expense Category
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                        {expense.expenseName}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, textTransform: 'uppercase' }}>
                                        Total Amount
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#3C0008' }}>
                                        ₹{expense.amount}
                                    </Typography>
                                </Box>
                            </Stack>

                            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Icon icon="mdi:calendar-clock" width={18} color="#64748b" />
                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                    Recorded on: {expense.date ? DateTime.fromISO(expense.date).toFormat("dd/MM/yyyy") : '—'}
                                </Typography>
                            </Box>
                        </Paper>

                        {/* Details Grid */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <DetailItem icon="mdi:storefront" label="Store" value={expense.storeDetails?.storeName || expense.storeName} color="#3C0008" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailItem icon="mdi:credit-card" label="Payment" value={expense.paymentMode} color="#3C0008" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailItem icon="mdi:label" label="Type" value={expense.expenseType} color="#3C0008" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <DetailItem
                                    icon="mdi:account"
                                    label="Employee"
                                    value={
                                        expense.employeeId?.name ||
                                        expense.employeeDetails?.name ||
                                        expense.employeeId?.[0]?.name ||
                                        expense.employeeName ||
                                        expense.employee ||
                                        'N/A'
                                    }
                                    color="#3C0008"
                                />
                            </Grid>
                        </Grid>

                        {/* Description Box */}
                        <Box>
                            <Typography variant="caption" color="textSecondary" fontWeight="800" sx={{ mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Description / Notes
                            </Typography>
                            <Paper sx={{
                                p: 2.5,
                                borderRadius: '16px',
                                bgcolor: '#ffffff',
                                border: '1px solid #e0f2fe',
                                position: 'relative'
                            }}>
                                <Icon
                                    icon="mdi:format-quote-open"
                                    width={24}
                                    style={{ position: 'absolute', left: 8, top: -12, color: '#3C0008' }}
                                />
                                <Typography variant="body2" sx={{ color: '#3C0008', lineHeight: 1.7, fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                                    {expense.description || "No description provided for this expense."}
                                </Typography>
                            </Paper>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            onClick={onClose}
                            sx={{
                                py: 2,
                                borderRadius: '16px',
                                bgcolor: '#3c0008',
                                fontWeight: 800,
                                fontSize: '0.9rem',
                                letterSpacing: '0.1em',
                                boxShadow: '0 10px 20px -5px rgba(15, 23, 42, 0.3)',
                                '&:hover': { bgcolor: '#3c0008', transform: 'translateY(-2px)' },
                                transition: '0.3s'
                            }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
};

export default ExpenseDetailsPopup;
